import { marketingOrigin } from '../common/siteEnvironment';
import React, { useEffect, useState } from 'react';
import { initializePaddle } from '../../services/paddleService';

const TRANSACTION_PATTERN = /^txn_[a-z0-9]+$/i;

const PaddleCheckoutPage: React.FC = () => {
    const transactionId = new URLSearchParams(window.location.search).get('_ptxn')?.trim() || '';
    const [error, setError] = useState('');
    const [isReady, setIsReady] = useState(false);
    const [isComplete, setIsComplete] = useState(false);

    useEffect(() => {
        document.title = 'Secure Checkout | IntelOptics';

        const completeCheckout = () => {
            setIsComplete(true);
            setError('');
            setIsReady(false);
        };
        window.addEventListener('inteloptics:paddle-checkout-completed', completeCheckout);

        if (!TRANSACTION_PATTERN.test(transactionId)) {
            setError('This payment link is invalid or incomplete.');
            return () => window.removeEventListener('inteloptics:paddle-checkout-completed', completeCheckout);
        }

        void initializePaddle()
            .then(() => {
                if (!window.Paddle)
                    throw new Error('Paddle failed to load.');
                setIsReady(true);
            })
            .catch(error => {
                console.error('Paddle payment-link initialization failed:', error);
                setError('Secure checkout could not load. Please try again.');
            });
        return () => window.removeEventListener('inteloptics:paddle-checkout-completed', completeCheckout);
    }, [transactionId]);

    const reopenCheckout = () => {
        try {
            window.Paddle.Checkout.open({
                transactionId,
                settings: {
                    displayMode: 'overlay',
                    theme: 'dark'
                }
            });
        } catch (error) {
            console.error('Paddle checkout reopen failed:', error);
            setError('Secure checkout could not reopen. Please try again.');
        }
    };

    return (
        <main className="min-h-screen bg-slate-950 px-4 py-8 text-slate-100 flex items-center justify-center">
            <section className="w-full max-w-lg rounded-3xl border border-slate-700 bg-slate-900/90 p-8 text-center shadow-2xl sm:p-12">
                <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-blue-600 text-xl font-black shadow-lg shadow-blue-950">
                    IO
                </div>
                <p className="text-xs font-bold uppercase tracking-[0.3em] text-blue-400">IntelOptics</p>
                <h1 className="mt-3 text-3xl font-black text-white">Secure Checkout</h1>

                {isComplete ? (
                    <div role="status">
                        <div className="mx-auto mt-7 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500/15 text-2xl text-emerald-400" aria-hidden="true">✓</div>
                        <h2 className="mt-5 text-xl font-extrabold text-white">Payment Complete</h2>
                        <p className="mt-3 text-sm leading-6 text-slate-300">Your payment was received. Eprims will complete your organization setup and contact you.</p>
                        <a className="mt-7 inline-flex rounded-xl bg-blue-600 px-6 py-3 text-sm font-bold text-white transition hover:bg-blue-500" href={marketingOrigin}>Return to Eprims</a>
                    </div>
                ) : error ? (
                    <>
                        <p className="mt-5 text-sm leading-6 text-red-300" role="alert">{error}</p>
                        <a
                            className="mt-7 inline-flex rounded-xl bg-blue-600 px-6 py-3 text-sm font-bold text-white transition hover:bg-blue-500"
                            href="mailto:contact@eprims.com?subject=Checkout%20Support"
                        >
                            Contact Support
                        </a>
                    </>
                ) : (
                    <>
                        <div className="mx-auto mt-8 h-10 w-10 animate-spin rounded-full border-4 border-slate-700 border-t-blue-500" aria-hidden="true" />
                        <p className="mt-5 text-sm leading-6 text-slate-300">
                            Your secure Paddle checkout should open automatically.
                        </p>
                        {isReady && (
                            <button
                                className="mt-7 rounded-xl border border-slate-600 px-6 py-3 text-sm font-bold text-white transition hover:border-blue-400 hover:bg-slate-800"
                                onClick={reopenCheckout}
                                type="button"
                            >
                                Reopen Secure Checkout
                            </button>
                        )}
                    </>
                )}

                <p className="mt-8 text-xs text-slate-500">Payments are securely processed by Paddle.</p>
            </section>
        </main>
    );
};

export default PaddleCheckoutPage;
